import React, {
  useState, useContext, useEffect, useCallback
} from 'react'
import { useLazyQuery } from '@apollo/react-hooks'
import { path, prop, compose } from 'ramda'
import { useSelector } from 'react-redux'

import NextEpisodeAndUserRatingQuery from './next-episode-and-user-rating.gql'
import NextEpisodeCountdown from './next-episode-countdown'
import { StateContext } from '../../state'
import { convertData } from '../../utils/episode-selection'
import { shouldShowBanner } from './utils/should-show-banner'
import { getDetailsUrl } from '../../../../lib/utils'
import MoreLikeThis from './more-like-this'
import PlayerBannerRateModal from '../../../../modules/user-rating/player-banner-rate-modal'
import { UserRatingScale } from '../../../../modules/user-rating/shared'
import PROFILE_PREFERENCES_QUERY from '../../../../../graphql/queries/profile-preferences.gql'
import { getSelectedProfileIdFromSession } from '../../../../lib/account'

const getNextEpisode = compose(prop('nextEpisode'), convertData)

/**
 * Banner Modals in Player
 *
 * Key features:
 * - Next episode autoplay countdown (series only)
 * - Rate modal (movie/series depending on state)
 * - More-like-this Carousel
 */

function Banner() {
  // when a modal has been dismissed by user, shouldShow* set to false
  const [shouldShowRate, setShouldShowRate] = useState(false)
  const [shouldShowMoreLikeThis, setShouldShowMoreLikeThis] = useState(true)
  const [shouldShowNextEpisode, setShouldShowNextEpisode] = useState(true)
  const [hasDecidedToShowRate, setHasDecidedToShowRate] = useState(false)
  const profileId = useSelector(state => getSelectedProfileIdFromSession(state.session))
  const [fetchProfilePreferences, { data: profilePreferencesData }] = useLazyQuery(PROFILE_PREFERENCES_QUERY, {
    fetchPolicy: 'cache-first'
  })

  const dismissRateModal = useCallback(() => {
    setShouldShowRate(false)
  }, [])
  const dismissMoreLikeThis = useCallback(() => {
    setShouldShowMoreLikeThis(false)
  }, [])
  const dismissNextEpisodeCountdown = useCallback(() => {
    setShouldShowNextEpisode(false)
  }, [])

  const {
    getMetaData,
    duration,
    currentTime,
    isContinueWatchingShown,
    contentId: contentIdFromState,
    getStreams,
    values,
    isTrailer,
    saveCurrentPosition,
    isPlaying,
    contentItem,
    setIsBannerRateModalShowing,
    setIsNextEpisodeCountdownShowing,
    isAdOnPauseShown
  } = useContext(StateContext)

  const isEpisode = contentItem.__typename === 'Episode'
  const [
    fetchData,
    { loading, error, data: episodeAndRatingData }
  ] = useLazyQuery(NextEpisodeAndUserRatingQuery)
  const autoplayEnabled = profilePreferencesData?.profilePreferences?.nextEpisodeAutoplayEnabled
  const countdownTime = values.PLAYER_AUTO_PLAY_COUNTDOWN
  const countdownThreshold = values.PLAYER_AUTO_PLAY_THRESHOLD
  /**
   * if shouldRenderBanner is false, all children of Banner (PlayerBannerRateModal, MoreLikeThis, NextEpisodeCountdown) won't show, and NextEpisodeAndUserRatingQuery won't be fired
   */
  const shouldRenderBanner = shouldShowBanner(
    getMetaData,
    duration,
    currentTime,
    isContinueWatchingShown,
    countdownThreshold,
    countdownTime,
    isAdOnPauseShown
  )

  useEffect(() => {
    if (shouldRenderBanner) {
      fetchData({
        variables: {
          contentId: contentIdFromState,
          userRatingInput: {
            contentItemId:
              isEpisode ? contentItem.series.id : contentItem.id
          }
        }
      })
      saveCurrentPosition('STOP')
    }
  }, [shouldRenderBanner, contentIdFromState, contentItem])

  /**
   * Fetch profile preferences only when needed:
   * current profile exists and a next episode is available.
   */
  useEffect(() => {
    if (isEpisode && shouldRenderBanner && episodeAndRatingData && profileId) {
      const nextEpisode = getNextEpisode(episodeAndRatingData)
      if (nextEpisode) {
        fetchProfilePreferences()
      }
    }
  }, [shouldRenderBanner, episodeAndRatingData, profileId])

  /**
   * Reset next-episode banner visibility for each banner cycle
   * when autoplay is enabled.
   */
  useEffect(() => {
    if (isEpisode && shouldRenderBanner && autoplayEnabled) {
      setShouldShowNextEpisode(true)
    }
  }, [isEpisode, shouldRenderBanner, autoplayEnabled])

  if (isTrailer || !shouldRenderBanner) return null

  if (!shouldShowRate && !shouldShowMoreLikeThis && !shouldShowNextEpisode) {
    return null
  }

  // wait for response of NextEpisodeAndUserRatingQuery
  if (loading || error || !episodeAndRatingData) return null

  const seriesOrMovie = isEpisode ? contentItem.series : contentItem

  const isMovie = !path(['contentItem', 'series'])(episodeAndRatingData)
  const showpageUrl = getDetailsUrl(episodeAndRatingData.contentItem)
  const userRating = episodeAndRatingData.userRating
  const isMovieOrSeriesRated = userRating && userRating.ratingValue !== UserRatingScale.UNRATED
  const isMovieOrSeriesRatedBad = isMovieOrSeriesRated && userRating.ratingValue === UserRatingScale.NOT_MY_THING

  // when episodeAndRatingData is fetched for the first time, remember whether we have decided to show rate based on isMovieOrSeriesRated,
  // and the following change of isMovieOrSeriesRated(after user rated on the modal which will change episodeAndRatingData) won't affect shouldShowRate,
  // after user dismissed the rate modal, shouldShowRate will be set to false
  if (!hasDecidedToShowRate) {
    if (!isMovieOrSeriesRated) {
      setShouldShowRate(true)
    }
    setHasDecidedToShowRate(true)
    return null // prevent reaching further rendering logic as we want a rerender due to change of shouldShowRate
  }

  if (isMovie) {
    if (shouldShowRate) {
      return (
        <PlayerBannerRateModal
          setIsBannerRateModalShowing={setIsBannerRateModalShowing}
          dismissRateModal={dismissRateModal}
          contentItem={seriesOrMovie}
        />
      )
    }
    if (!shouldShowRate && shouldShowMoreLikeThis && !isMovieOrSeriesRatedBad) {
      return (
        <MoreLikeThis
          showpageUrl={showpageUrl}
          dismissMoreLikeThis={dismissMoreLikeThis}
        />
      )
    }
  } else {
    const nextEpisode = getNextEpisode(episodeAndRatingData)

    /**
     * Series priority:
     * 1) NextEpisodeCountdown (if next episode + autoplay enabled + not dismissed)
     * 2) Rate (if no next episode)
     * 3) MoreLikeThis (if no next episode and not negatively rated)
     */
    if (nextEpisode && shouldShowNextEpisode && autoplayEnabled) {
      let displayCountdownTime = countdownTime
      const roundedCurrentTime = Math.floor(currentTime)
      if (duration - roundedCurrentTime < displayCountdownTime) {
        displayCountdownTime = duration - roundedCurrentTime
      }
      return (
        <NextEpisodeCountdown
          nextEpisode={nextEpisode}
          currentEpisodeInfo={convertData(episodeAndRatingData).current}
          getStreams={getStreams}
          countdownTime={displayCountdownTime}
          dismissBanner={dismissNextEpisodeCountdown}
          setIsNextEpisodeCountdownShowing={setIsNextEpisodeCountdownShowing}
          isPlaying={isPlaying}
        />
      )
    }
    if (!nextEpisode && shouldShowRate) {
      return (
        <PlayerBannerRateModal
          setIsBannerRateModalShowing={setIsBannerRateModalShowing}
          dismissRateModal={dismissRateModal}
          contentItem={seriesOrMovie}
        />
      )
    }
    if (!nextEpisode && !shouldShowRate && shouldShowMoreLikeThis && !isMovieOrSeriesRatedBad) {
      return (
        <MoreLikeThis
          showpageUrl={showpageUrl}
          dismissMoreLikeThis={dismissMoreLikeThis}
        />
      )
    }
  }

  return null
}

export default Banner
